import styles from "./AccommodationModal.module.css";
import type { AccommodationItem } from "@/app/student-life/accommodation/types";
import { useEffect } from "react";

type AccommodationModalLabels = {
  about: string;
  pros: string;
  cons: string;
  location: string;
  openMaps: string;
};

type AccommodationModalProps = {
  item: AccommodationItem | null;
  onClose: () => void;
  labels: AccommodationModalLabels;
};

export default function AccommodationModal({
  item,
  onClose,
  labels,
}: AccommodationModalProps) {
  useEffect(() => {
    if (!item) return;

    const scrollY = window.scrollY;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";

      window.scrollTo(0, scrollY);
    };
  }, [item]);

  if (!item) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="accommodation-modal-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="accommodation-modal-title" className={styles.modalTitle}>
            {item.title}
          </h2>

          <button
            type="button"
            className={styles.modalCloseBtn}
            onClick={onClose}
            aria-label="Close details modal"
          >
            ×
          </button>
        </div>

        <div className={styles.modalContent}>
          <section className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>{labels.about}</h3>
            <p className={styles.modalText}>{item.about}</p>
          </section>

          <div className={styles.modalDivider} />

          <section className={styles.priceCard}>
            <div className={styles.priceIcon}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M46.3746 5.4244C46.2683 4.4553 45.8326 3.55202 45.1404 2.86551C44.4482 2.17901 43.5413 1.75087 42.5714 1.65265L28.0301 0.0239375C27.3895 -0.0434228 26.7419 0.0318075 26.1337 0.244233C25.5256 0.456659 24.972 0.801017 24.5127 1.25261L1.25348 24.5375C0.450837 25.3411 0 26.4305 0 27.5663C0 28.7021 0.450837 29.7915 1.25348 30.5951L17.4006 46.7422C17.7954 47.1433 18.2666 47.4613 18.7863 47.6772C19.306 47.8931 19.8638 48.0027 20.4266 47.9995C20.9906 48.0084 21.5505 47.9014 22.0715 47.6852C22.5925 47.4689 23.0635 47.148 23.4554 46.7422L46.746 23.4802C47.1993 23.0233 47.5449 22.4709 47.7574 21.8633C47.97 21.2558 48.0442 20.6084 47.9747 19.9685L46.3746 5.4244ZM27.4872 23.826C27.6215 23.9588 27.7282 24.117 27.801 24.2914C27.8738 24.4658 27.9112 24.6528 27.9112 24.8418C27.9112 25.0307 27.8738 25.2178 27.801 25.3922C27.7282 25.5665 27.6215 25.7247 27.4872 25.8576C27.3583 25.9931 27.2031 26.1011 27.0312 26.1748C26.8593 26.2486 26.6742 26.2866 26.4871 26.2866C26.3 26.2866 26.1149 26.2486 25.943 26.1748C25.7711 26.1011 25.6159 25.9931 25.487 25.8576L23.8583 24.226L23.0011 23.4002C22.8234 23.2218 22.5821 23.1212 22.3302 23.1207C22.0783 23.1201 21.8366 23.2197 21.6581 23.3974C21.4796 23.5751 21.3791 23.8164 21.3785 24.0683C21.378 24.3201 21.4775 24.5619 21.6553 24.7403L23.2554 26.3405C23.9706 27.0577 24.3723 28.0293 24.3723 29.0421C24.3723 30.055 23.9706 31.0266 23.2554 31.7438C22.5342 32.4394 21.5715 32.8286 20.5695 32.8296C19.6891 32.8361 18.8367 32.5212 18.1721 31.9438L17.4578 32.6582C17.3244 32.7916 17.166 32.8974 16.9917 32.9696C16.8174 33.0418 16.6306 33.0789 16.4419 33.0789C16.2533 33.0789 16.0665 33.0418 15.8922 32.9696C15.7179 32.8974 15.5595 32.7916 15.4261 32.6582C15.2927 32.5248 15.1869 32.3664 15.1147 32.1921C15.0425 32.0178 15.0054 31.831 15.0054 31.6424C15.0054 31.4537 15.0425 31.2669 15.1147 31.0926C15.1869 30.9183 15.2927 30.76 15.4261 30.6266L16.1119 29.9722L15.4261 29.2865C15.1567 29.017 15.0054 28.6516 15.0054 28.2706C15.0054 27.8896 15.1567 27.5243 15.4261 27.2548C15.6956 26.9854 16.0609 26.8341 16.4419 26.8341C16.8229 26.8341 17.1883 26.9854 17.4578 27.2548L19.0579 28.8864L19.8837 29.7122C20.0672 29.8909 20.3133 29.9909 20.5695 29.9909C20.8256 29.9909 21.0717 29.8909 21.2552 29.7122C21.3434 29.6243 21.4134 29.5199 21.4612 29.4049C21.5089 29.2899 21.5335 29.1667 21.5335 29.0421C21.5335 28.9176 21.5089 28.7944 21.4612 28.6794C21.4134 28.5644 21.3434 28.46 21.2552 28.3721L19.6294 26.7719C18.9262 26.0714 18.524 25.1242 18.5083 24.1317C18.4926 23.1393 18.8647 22.1798 19.5454 21.4574C20.2261 20.735 21.1618 20.3067 22.1534 20.2634C23.1451 20.2201 24.1145 20.5654 24.8555 21.2258L25.5413 20.54C25.8129 20.3039 26.1637 20.1789 26.5234 20.1902C26.8832 20.2015 27.2254 20.3482 27.4817 20.6008C27.738 20.8535 27.8895 21.1936 27.9059 21.5532C27.9222 21.9127 27.8023 22.2652 27.5701 22.5402L26.8871 23.2259L27.4872 23.826ZM40.6598 11.3992C40.2592 11.7965 39.7501 12.0663 39.1964 12.1746C38.6428 12.283 38.0695 12.225 37.5487 12.0081C37.028 11.7911 36.5831 11.4249 36.2701 10.9555C35.9572 10.4861 35.7902 9.93459 35.7902 9.37045C35.7902 8.80631 35.9572 8.2548 36.2701 7.78541C36.5831 7.31602 37.028 6.94977 37.5487 6.73282C38.0695 6.51586 38.6428 6.45791 39.1964 6.56627C39.7501 6.67463 40.2592 6.94444 40.6598 7.34171C41.1942 7.88108 41.4941 8.60969 41.4941 9.36902C41.4941 10.1284 41.1942 10.8598 40.6598 11.3992Z" fill="currentColor" />
              </svg>
            </div>

            <div>
              <h3 className={styles.priceTitle}>{item.priceRange}</h3>
              <p className={styles.priceNote}>{item.priceNote}</p>
            </div>
          </section>

          <section className={styles.prosConsGrid}>
            <div className={styles.prosCard}>
              <div className={styles.prosConsHeader}>
                <svg className={styles.prosIcon} width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.63589 15.272H3.31281C1.48458 15.272 0 16.7618 0 18.5965V44.034C0 45.8687 1.48458 47.3585 3.31281 47.3585H5.63589C7.46412 47.3585 8.9487 45.8687 8.9487 44.034V18.5965C8.9487 16.7618 7.46412 15.272 5.63589 15.272Z" fill="currentColor"/>
                  <path d="M50.8124 16.5618H27.3959C29.0248 12.4096 30.6538 8.25049 32.2827 4.09828C32.4064 2.31186 31.0661 0.670287 29.073 0.159882C27.3135 -0.295344 25.4303 0.24265 24.3581 1.50487C20.1792 6.51925 16.0004 11.5405 11.8216 16.5618C11.801 18.1551 11.7873 19.0104 11.7873 19.0104C11.7873 19.0104 11.7873 19.9553 11.7873 19.976C11.7873 21.721 11.7873 35.5847 11.7873 44.5444C11.7873 46.455 13.21 48 14.9695 48H38.8189C39.9942 48 41.0802 47.2965 41.63 46.1653C45.6301 37.9919 49.6233 29.8185 53.6235 21.6452C54.7506 19.3415 53.2111 16.5618 50.8124 16.5618Z" fill="currentColor"/>
                </svg>

                <h3 className={styles.prosTitle}>{labels.pros}</h3>
              </div>

              <ul className={styles.prosConsList}>
                {item.pros.map((point, index) => (
                  <li key={index} className={styles.prosItem}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className={styles.consCard}>
              <div className={styles.prosConsHeader}>
                <svg className={styles.consIcon} width="54" height="48" viewBox="0 0 54 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.31554 0.631112H5.63983C7.47192 0.631112 8.95537 2.11972 8.95537 3.95817V29.3948C8.95537 31.2332 7.47192 32.7218 5.63983 32.7218H3.31554C1.48344 32.7218 -1.69085e-06 31.2332 -1.69085e-06 29.3948V3.95817C-1.69085e-06 2.11972 1.48344 0.631112 3.31554 0.631112Z" fill="currentColor"/>
                  <path d="M14.978 0H38.8226C39.9984 0 41.0785 0.706572 41.6322 1.83846C45.6314 10.0086 49.6305 18.1856 53.6229 26.3558C54.7508 28.6607 53.2127 31.439 50.8132 31.439H27.3993C29.0195 35.5755 30.6397 39.7189 32.2667 43.8555C32.2804 43.8898 32.2872 43.9172 32.2872 43.9515C32.3829 45.7145 31.0498 47.3334 29.0742 47.8411C27.3105 48.2938 25.4374 47.7588 24.3572 46.4965C20.1803 41.4751 16.0035 36.4605 11.8197 31.439C11.7992 29.8406 11.7855 28.99 11.7855 28.99C11.7855 28.99 11.7855 28.0433 11.7855 28.0296C11.7855 26.2872 11.7855 12.4165 11.7855 3.46426C11.7924 1.54348 13.2211 0 14.978 0Z" fill="currentColor"/>
                </svg>

                <h3 className={styles.consTitle}>{labels.cons}</h3>
              </div>

              <ul className={styles.prosConsList}>
                {item.cons.map((point, index) => (
                  <li key={index} className={styles.consItem}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <div className={styles.modalDivider} />

          <section className={styles.modalSection}>
            <h3 className={styles.modalSectionTitle}>{labels.location}</h3>
            <p className={styles.locationText}>{item.locationText}</p>

            {item.mapEmbedUrl ? (
              <iframe
                className={styles.mapPlaceholder}
                src={item.mapEmbedUrl}
                style={{
                  width: "100%",
                  height: "350px",
                  border: "none",
                  borderRadius: "8px",
                  marginTop: "1rem",
                  marginBottom: "1rem",
                }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Location map"
              />
            ) : (
              <div className={styles.mapPlaceholder} />
            )}

            <a
              href={item.mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.mapLink}
            >
              {labels.openMaps}
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}
